import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database';

type ClientRow = Database['public']['Tables']['clients']['Row'];
type ColleagueRow = Database['public']['Tables']['colleagues']['Row'];
type GuardianRow = Database['public']['Tables']['guardians']['Row'];

type PersonaSubTab = 'clients' | 'colleagues' | 'guardians';

// ---------- shared helpers ----------

const ArrayField: React.FC<{
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
}> = ({ label, value, onChange }) => {
  const [input, setInput] = useState('');
  return (
    <div>
      <label className="block text-xs font-medium text-[#555555] mb-1">{label}</label>
      <div className="flex gap-2 mb-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && input.trim()) {
              onChange([...value, input.trim()]);
              setInput('');
            }
          }}
          placeholder="Add item, press Enter"
          className="border border-[#DADADA] rounded px-2 py-1 text-sm flex-1 focus:outline-none focus:border-[#1A1A1A]"
        />
        <button
          type="button"
          onClick={() => {
            if (input.trim()) {
              onChange([...value, input.trim()]);
              setInput('');
            }
          }}
          className="px-2 py-1 rounded bg-[#F5F5F5] hover:bg-[#EBEBEB] text-[#555555]"
        >
          <Plus size={14} />
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {value.map((item, i) => (
          <span
            key={i}
            className="flex items-center gap-1 bg-[#F5F5F5] rounded px-2 py-0.5 text-xs text-[#555555]"
          >
            {item}
            <button
              type="button"
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              className="text-[#888888] hover:text-red-500"
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
}> = ({ label, value, onChange, multiline }) => (
  <div>
    <label className="block text-xs font-medium text-[#555555] mb-1">{label}</label>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] resize-none"
      />
    ) : (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A]"
      />
    )}
  </div>
);

// ---------- Clients ----------

const defaultClient = (): Omit<ClientRow, 'created_at' | 'updated_at'> => ({
  id: '',
  guardian_id: null,
  name: '',
  avatar_url: null,
  age: 8,
  diagnosis: '',
  style: '',
  start_date: new Date().toISOString().split('T')[0],
  concerns: [],
  strengths: [],
  reinforcers: [],
  triggers: [],
  current_targets: [],
  techniques: [],
  availability: 'coming_soon',
});

const ClientForm: React.FC<{
  initial: Omit<ClientRow, 'created_at' | 'updated_at'>;
  onSave: (data: Omit<ClientRow, 'created_at' | 'updated_at'>) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}> = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="border border-[#DADADA] rounded-xl p-4 bg-[#FAFAFA] space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="ID (slug)" value={form.id} onChange={(v) => set('id', v)} />
        <Field label="Name" value={form.name} onChange={(v) => set('name', v)} />
        <Field label="Age" value={String(form.age)} onChange={(v) => set('age', Number(v))} />
        <Field label="Diagnosis" value={form.diagnosis} onChange={(v) => set('diagnosis', v)} />
        <Field label="Style" value={form.style} onChange={(v) => set('style', v)} />
        <Field label="Start Date" value={form.start_date} onChange={(v) => set('start_date', v)} />
        <Field label="Guardian ID" value={form.guardian_id ?? ''} onChange={(v) => set('guardian_id', v || null)} />
        <Field label="Avatar URL" value={form.avatar_url ?? ''} onChange={(v) => set('avatar_url', v || null)} />
        <div>
          <label className="block text-xs font-medium text-[#555555] mb-1">Learner catalog</label>
          <select
            value={form.availability}
            onChange={(e) => set('availability', e.target.value as ClientRow['availability'])}
            className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] bg-white"
          >
            <option value="available">Available</option>
            <option value="coming_soon">Coming soon</option>
            <option value="draft">Draft (hidden from learners)</option>
          </select>
        </div>
      </div>
      <ArrayField label="Concerns" value={form.concerns} onChange={(v) => set('concerns', v)} />
      <ArrayField label="Strengths" value={form.strengths} onChange={(v) => set('strengths', v)} />
      <ArrayField label="Reinforcers" value={form.reinforcers} onChange={(v) => set('reinforcers', v)} />
      <ArrayField label="Triggers" value={form.triggers} onChange={(v) => set('triggers', v)} />
      <ArrayField label="Current Targets" value={form.current_targets} onChange={(v) => set('current_targets', v)} />
      <ArrayField label="Techniques" value={form.techniques} onChange={(v) => set('techniques', v)} />
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => void onSave(form)}
          disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333] disabled:opacity-50"
        >
          <Check size={14} />
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1.5 border border-[#DADADA] text-sm rounded-lg hover:bg-[#F5F5F5] text-[#555555]"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </div>
  );
};

const ClientsTab: React.FC = () => {
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetch = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    const { data, error: e } = await supabase.from('clients').select('*').order('name');
    if (e) setError(e.message);
    else setRows(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void fetch(); }, [fetch]);

  const saveNew = async (form: Omit<ClientRow, 'created_at' | 'updated_at'>) => {
    if (!supabase) return;
    setSaving(true);
    const { error: e } = await supabase.from('clients').insert(form);
    if (e) { setError(e.message); setSaving(false); return; }
    setAdding(false);
    setSaving(false);
    void fetch();
  };

  const saveEdit = async (form: Omit<ClientRow, 'created_at' | 'updated_at'>) => {
    if (!supabase) return;
    setSaving(true);
    const { error: e } = await supabase.from('clients').update(form).eq('id', form.id);
    if (e) { setError(e.message); setSaving(false); return; }
    setEditing(null);
    setSaving(false);
    void fetch();
  };

  const remove = async (id: string) => {
    if (!supabase || !confirm('Delete this client? This cannot be undone.')) return;
    const { error: e } = await supabase.from('clients').delete().eq('id', id);
    if (e) setError(e.message);
    else void fetch();
  };

  const toggleExpanded = (id: string) =>
    setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-3">
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      <div className="flex justify-between items-center">
        <span className="text-sm text-[#555555]">{rows.length} clients</span>
        <div className="flex gap-2">
          <button onClick={() => void fetch()} className="flex items-center gap-1 text-sm text-[#555555] hover:text-[#1A1A1A]"><RefreshCw size={13} /> Refresh</button>
          <button onClick={() => setAdding(true)} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333]"><Plus size={14} /> Add Client</button>
        </div>
      </div>

      {adding && (
        <ClientForm initial={defaultClient()} onSave={saveNew} onCancel={() => setAdding(false)} saving={saving} />
      )}

      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.id} className="border border-[#DADADA] rounded-xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="font-medium text-[#1A1A1A]">{row.name}</span>
                <span className="ml-2 text-xs text-[#888888]">{row.diagnosis} · Age {row.age}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#F0F0F0] text-[#555555]">
                  {row.availability.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleExpanded(row.id)} className="text-[#888888] hover:text-[#1A1A1A]">
                  {expanded.has(row.id) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                <button onClick={() => setEditing(row.id)} className="text-[#888888] hover:text-[#1A1A1A]"><Pencil size={15} /></button>
                <button onClick={() => void remove(row.id)} className="text-[#888888] hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            </div>

            {expanded.has(row.id) && editing !== row.id && (
              <div className="border-t border-[#DADADA] px-4 py-3 grid grid-cols-2 gap-x-8 gap-y-1 text-sm text-[#555555]">
                <div><span className="font-medium text-[#1A1A1A]">Style:</span> {row.style}</div>
                <div><span className="font-medium text-[#1A1A1A]">Start:</span> {row.start_date}</div>
                {row.concerns.length > 0 && <div className="col-span-2"><span className="font-medium text-[#1A1A1A]">Concerns:</span> {row.concerns.join(', ')}</div>}
                {row.strengths.length > 0 && <div className="col-span-2"><span className="font-medium text-[#1A1A1A]">Strengths:</span> {row.strengths.join(', ')}</div>}
                {row.reinforcers.length > 0 && <div className="col-span-2"><span className="font-medium text-[#1A1A1A]">Reinforcers:</span> {row.reinforcers.join(', ')}</div>}
                {row.triggers.length > 0 && <div className="col-span-2"><span className="font-medium text-[#1A1A1A]">Triggers:</span> {row.triggers.join(', ')}</div>}
                {row.current_targets.length > 0 && <div className="col-span-2"><span className="font-medium text-[#1A1A1A]">Targets:</span> {row.current_targets.join(', ')}</div>}
                {row.techniques.length > 0 && <div className="col-span-2"><span className="font-medium text-[#1A1A1A]">Techniques:</span> {row.techniques.join(', ')}</div>}
              </div>
            )}

            {editing === row.id && (
              <div className="border-t border-[#DADADA] p-4">
                <ClientForm
                  initial={{ id: row.id, guardian_id: row.guardian_id, name: row.name, avatar_url: row.avatar_url, age: row.age, diagnosis: row.diagnosis, style: row.style, start_date: row.start_date, concerns: row.concerns, strengths: row.strengths, reinforcers: row.reinforcers, triggers: row.triggers, current_targets: row.current_targets, techniques: row.techniques, availability: row.availability }}
                  onSave={saveEdit}
                  onCancel={() => setEditing(null)}
                  saving={saving}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Colleagues ----------

const defaultColleague = (): Omit<ColleagueRow, 'created_at' | 'updated_at'> => ({
  id: '',
  name: '',
  avatar_url: null,
  title: '',
  organization: '',
  specialty: '',
  personality: '',
  communication_style: '',
  expertise: [],
  working_since: '',
  background_info: '',
  preferred_communication: '',
  availability: 'coming_soon',
});

const ColleagueForm: React.FC<{
  initial: Omit<ColleagueRow, 'created_at' | 'updated_at'>;
  onSave: (data: Omit<ColleagueRow, 'created_at' | 'updated_at'>) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}> = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="border border-[#DADADA] rounded-xl p-4 bg-[#FAFAFA] space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="ID (slug)" value={form.id} onChange={(v) => set('id', v)} />
        <Field label="Name" value={form.name} onChange={(v) => set('name', v)} />
        <Field label="Title" value={form.title} onChange={(v) => set('title', v)} />
        <Field label="Organization" value={form.organization} onChange={(v) => set('organization', v)} />
        <Field label="Specialty" value={form.specialty} onChange={(v) => set('specialty', v)} />
        <Field label="Working Since" value={form.working_since} onChange={(v) => set('working_since', v)} />
        <Field label="Avatar URL" value={form.avatar_url ?? ''} onChange={(v) => set('avatar_url', v || null)} />
        <Field label="Preferred Communication" value={form.preferred_communication} onChange={(v) => set('preferred_communication', v)} />
        <div>
          <label className="block text-xs font-medium text-[#555555] mb-1">Learner catalog</label>
          <select
            value={form.availability}
            onChange={(e) => set('availability', e.target.value as ColleagueRow['availability'])}
            className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] bg-white"
          >
            <option value="available">Available</option>
            <option value="coming_soon">Coming soon</option>
            <option value="draft">Draft (hidden from learners)</option>
          </select>
        </div>
      </div>
      <Field label="Personality" value={form.personality} onChange={(v) => set('personality', v)} multiline />
      <Field label="Communication Style" value={form.communication_style} onChange={(v) => set('communication_style', v)} multiline />
      <Field label="Background Info" value={form.background_info} onChange={(v) => set('background_info', v)} multiline />
      <ArrayField label="Expertise" value={form.expertise} onChange={(v) => set('expertise', v)} />
      <div className="flex gap-2 pt-1">
        <button onClick={() => void onSave(form)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333] disabled:opacity-50">
          <Check size={14} />{saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} className="flex items-center gap-1 px-3 py-1.5 border border-[#DADADA] text-sm rounded-lg hover:bg-[#F5F5F5] text-[#555555]">
          <X size={14} />Cancel
        </button>
      </div>
    </div>
  );
};

const ColleaguesTab: React.FC = () => {
  const [rows, setRows] = useState<ColleagueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    const { data, error: e } = await supabase.from('colleagues').select('*').order('name');
    if (e) setError(e.message);
    else setRows(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void fetch(); }, [fetch]);

  const saveNew = async (form: Omit<ColleagueRow, 'created_at' | 'updated_at'>) => {
    if (!supabase) return;
    setSaving(true);
    const { error: e } = await supabase.from('colleagues').insert(form);
    if (e) { setError(e.message); setSaving(false); return; }
    setAdding(false); setSaving(false); void fetch();
  };

  const saveEdit = async (form: Omit<ColleagueRow, 'created_at' | 'updated_at'>) => {
    if (!supabase) return;
    setSaving(true);
    const { error: e } = await supabase.from('colleagues').update(form).eq('id', form.id);
    if (e) { setError(e.message); setSaving(false); return; }
    setEditing(null); setSaving(false); void fetch();
  };

  const remove = async (id: string) => {
    if (!supabase || !confirm('Delete this colleague?')) return;
    const { error: e } = await supabase.from('colleagues').delete().eq('id', id);
    if (e) setError(e.message);
    else void fetch();
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-3">
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      <div className="flex justify-between items-center">
        <span className="text-sm text-[#555555]">{rows.length} colleagues</span>
        <div className="flex gap-2">
          <button onClick={() => void fetch()} className="flex items-center gap-1 text-sm text-[#555555] hover:text-[#1A1A1A]"><RefreshCw size={13} /> Refresh</button>
          <button onClick={() => setAdding(true)} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333]"><Plus size={14} /> Add Colleague</button>
        </div>
      </div>

      {adding && <ColleagueForm initial={defaultColleague()} onSave={saveNew} onCancel={() => setAdding(false)} saving={saving} />}

      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.id} className="border border-[#DADADA] rounded-xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="font-medium text-[#1A1A1A]">{row.name}</span>
                <span className="ml-2 text-xs text-[#888888]">{row.title} · {row.organization}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#F0F0F0] text-[#555555]">
                  {row.availability.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(editing === row.id ? null : row.id)} className="text-[#888888] hover:text-[#1A1A1A]"><Pencil size={15} /></button>
                <button onClick={() => void remove(row.id)} className="text-[#888888] hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            </div>
            {editing === row.id && (
              <div className="border-t border-[#DADADA] p-4">
                <ColleagueForm
                  initial={{ id: row.id, name: row.name, avatar_url: row.avatar_url, title: row.title, organization: row.organization, specialty: row.specialty, personality: row.personality, communication_style: row.communication_style, expertise: row.expertise, working_since: row.working_since, background_info: row.background_info, preferred_communication: row.preferred_communication, availability: row.availability }}
                  onSave={saveEdit}
                  onCancel={() => setEditing(null)}
                  saving={saving}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Guardians ----------

const defaultGuardian = (): Omit<GuardianRow, 'created_at' | 'updated_at'> => ({
  id: '',
  name: '',
  relationship: '',
  personality: '',
  communication_style: '',
  concerns: [],
  strengths: [],
  preferred_communication: '',
  background_info: '',
});

const GuardianForm: React.FC<{
  initial: Omit<GuardianRow, 'created_at' | 'updated_at'>;
  onSave: (data: Omit<GuardianRow, 'created_at' | 'updated_at'>) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}> = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="border border-[#DADADA] rounded-xl p-4 bg-[#FAFAFA] space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="ID (slug)" value={form.id} onChange={(v) => set('id', v)} />
        <Field label="Name" value={form.name} onChange={(v) => set('name', v)} />
        <Field label="Relationship" value={form.relationship} onChange={(v) => set('relationship', v)} />
        <Field label="Preferred Communication" value={form.preferred_communication} onChange={(v) => set('preferred_communication', v)} />
      </div>
      <Field label="Personality" value={form.personality} onChange={(v) => set('personality', v)} multiline />
      <Field label="Communication Style" value={form.communication_style} onChange={(v) => set('communication_style', v)} multiline />
      <Field label="Background Info" value={form.background_info} onChange={(v) => set('background_info', v)} multiline />
      <ArrayField label="Concerns" value={form.concerns} onChange={(v) => set('concerns', v)} />
      <ArrayField label="Strengths" value={form.strengths} onChange={(v) => set('strengths', v)} />
      <div className="flex gap-2 pt-1">
        <button onClick={() => void onSave(form)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333] disabled:opacity-50">
          <Check size={14} />{saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} className="flex items-center gap-1 px-3 py-1.5 border border-[#DADADA] text-sm rounded-lg hover:bg-[#F5F5F5] text-[#555555]">
          <X size={14} />Cancel
        </button>
      </div>
    </div>
  );
};

const GuardiansTab: React.FC = () => {
  const [rows, setRows] = useState<GuardianRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    const { data, error: e } = await supabase.from('guardians').select('*').order('name');
    if (e) setError(e.message);
    else setRows(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void fetch(); }, [fetch]);

  const saveNew = async (form: Omit<GuardianRow, 'created_at' | 'updated_at'>) => {
    if (!supabase) return;
    setSaving(true);
    const { error: e } = await supabase.from('guardians').insert(form);
    if (e) { setError(e.message); setSaving(false); return; }
    setAdding(false); setSaving(false); void fetch();
  };

  const saveEdit = async (form: Omit<GuardianRow, 'created_at' | 'updated_at'>) => {
    if (!supabase) return;
    setSaving(true);
    const { error: e } = await supabase.from('guardians').update(form).eq('id', form.id);
    if (e) { setError(e.message); setSaving(false); return; }
    setEditing(null); setSaving(false); void fetch();
  };

  const remove = async (id: string) => {
    if (!supabase || !confirm('Delete this guardian?')) return;
    const { error: e } = await supabase.from('guardians').delete().eq('id', id);
    if (e) setError(e.message);
    else void fetch();
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-3">
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      <div className="flex justify-between items-center">
        <span className="text-sm text-[#555555]">{rows.length} guardians</span>
        <div className="flex gap-2">
          <button onClick={() => void fetch()} className="flex items-center gap-1 text-sm text-[#555555] hover:text-[#1A1A1A]"><RefreshCw size={13} /> Refresh</button>
          <button onClick={() => setAdding(true)} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333]"><Plus size={14} /> Add Guardian</button>
        </div>
      </div>

      {adding && <GuardianForm initial={defaultGuardian()} onSave={saveNew} onCancel={() => setAdding(false)} saving={saving} />}

      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.id} className="border border-[#DADADA] rounded-xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="font-medium text-[#1A1A1A]">{row.name}</span>
                <span className="ml-2 text-xs text-[#888888]">{row.relationship}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(editing === row.id ? null : row.id)} className="text-[#888888] hover:text-[#1A1A1A]"><Pencil size={15} /></button>
                <button onClick={() => void remove(row.id)} className="text-[#888888] hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            </div>
            {editing === row.id && (
              <div className="border-t border-[#DADADA] p-4">
                <GuardianForm
                  initial={{ id: row.id, name: row.name, relationship: row.relationship, personality: row.personality, communication_style: row.communication_style, concerns: row.concerns, strengths: row.strengths, preferred_communication: row.preferred_communication, background_info: row.background_info }}
                  onSave={saveEdit}
                  onCancel={() => setEditing(null)}
                  saving={saving}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Main PersonasAdmin ----------

const subTabs: { id: PersonaSubTab; label: string }[] = [
  { id: 'clients', label: 'Clients' },
  { id: 'colleagues', label: 'Colleagues' },
  { id: 'guardians', label: 'Guardians' },
];

const PersonasAdmin: React.FC = () => {
  const [sub, setSub] = useState<PersonaSubTab>('clients');

  return (
    <div>
      <div className="flex gap-1 mb-6">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              sub === t.id
                ? 'bg-[#1A1A1A] text-white'
                : 'bg-[#F5F5F5] text-[#555555] hover:bg-[#EBEBEB]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sub === 'clients' && <ClientsTab />}
      {sub === 'colleagues' && <ColleaguesTab />}
      {sub === 'guardians' && <GuardiansTab />}
    </div>
  );
};

export default PersonasAdmin;
